/**
 * Content API.
 *
 * The only module features and pages import content from. Keeping the lookups
 * here means components never search arrays themselves, and the backing store
 * can move to a CMS without touching a single component.
 */

import type { Project, Service } from '@/types/content'
import { projects } from './projects'
import { services } from './services'

export { awards, education, experience, service } from './experience'
export { projects } from './projects'
export { categoryName, domains, findDomain } from './domains'
export { services } from './services'
export { skillGroups } from './skills'
export {
  distinctions,
  references,
  research,
  researchInterests,
} from './research'
export { testimonials } from './testimonials'
export { expertise, expertiseStatement, tools } from './tools'
export {
  CONTACT_CHANNELS,
  LANGUAGES,
  NAV_ITEMS,
  SOCIAL_LINKS,
  site,
} from './site'

/* ---- Projects --------------------------------------------------------- */

export const findProject = (slug: string): Project | undefined =>
  projects.find((project) => project.slug === slug)

export const getFeaturedProjects = (): Project[] =>
  projects.filter((project) => project.featured)

export const getSecondaryProjects = (): Project[] =>
  projects.filter((project) => !project.featured)

/**
 * Other projects to suggest at the end of a case study.
 *
 * Takes the list to search rather than closing over the compiled-in
 * `projects` array, so a caller with the live CMS content — which is what
 * every page actually renders — gets suggestions drawn from it. Reading the
 * static array here silently showed placeholder covers and stale titles for
 * anything added or edited through the admin panel.
 *
 * A random sample, not the first `limit` in list order: taking the first
 * three always surfaced the same handful of projects at the top of the
 * document and left everything after them never suggested. Fisher–Yates on
 * a copy, so the caller's array is untouched and every candidate has an
 * equal chance regardless of where it sits in the list.
 */
export const getRelatedProjects = (
  from: Project[],
  slug: string,
  limit = 3,
): Project[] => {
  const candidates = from.filter((project) => project.slug !== slug)
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[candidates[i], candidates[j]] = [candidates[j], candidates[i]]
  }
  return candidates.slice(0, limit)
}

/* ---- Services --------------------------------------------------------- */

export const findService = (slug: string): Service | undefined =>
  services.find((service) => service.slug === slug)

/** Services belonging to a domain. */
export const servicesForDomain = (domainId: string): Service[] =>
  services.filter((service) => service.domain === domainId)

/** Projects classified under a domain, de-duplicated. */
export const projectsForDomain = (domainId: string): Project[] =>
  projects.filter((project) =>
    project.classifications.some((c) => c.domain === domainId),
  )
